import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Card, Button, Progress, Spin, notification } from 'antd';
import { CheckCircleOutlined, RocketOutlined } from '@ant-design/icons';
import { getOnboardingStatus } from '../../API/services/onboardingService';
import { dismissOnboarding } from '../../utils/onboardingStorage';
import { t } from '../../i18n/i18n';
import { CONFIGURATION, CREATE_EVALUATION, NEW_SEARCH_SEND } from '../../constants/routes';
import {
  setNewSearchData,
  setSearchWasAlreadyCreated,
  setSendEmailFrom,
  setSubjectEmail,
} from '../../redux/actions/screeningActions';
import { getCustomSearch } from '../../API/services/screeningService';
import './PartnerOnboarding.scss';

const PartnerOnboarding = ({ workspaceId, userId, onDismiss }) => {
  const [loading, setLoading] = useState(true);
  const [steps, setSteps] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [latestRecruitSessionId, setLatestRecruitSessionId] = useState(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const isMountedRef = useRef(true);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  const handleInviteAction = async () => {
    if (!latestRecruitSessionId) {
      history.push(CREATE_EVALUATION);
      return;
    }

    try {
      const evaluation = await getCustomSearch(latestRecruitSessionId);

      dispatch(
        setNewSearchData({
          ...evaluation,
          step: 4,
        })
      );
      dispatch(setSubjectEmail({ subjectEmail: '' }));
      dispatch(setSearchWasAlreadyCreated(true));
      dispatch(setSendEmailFrom('onboarding'));

      history.push(NEW_SEARCH_SEND);
    } catch {
      notification.error({
        message: t('ONBOARDING_ERROR_LOADING_EVALUATION'),
        placement: 'topRight',
      });
      history.push(CREATE_EVALUATION);
    }
  };

  const fetchOnboardingStatus = async () => {
    if (isMountedRef.current) {
      setLoading(true);
    }

    try {
      const status = await getOnboardingStatus(workspaceId);

      if (isMountedRef.current && status.latestRecruitSessionId) {
        setLatestRecruitSessionId(status.latestRecruitSessionId);
      }

      const stepsConfig = [
        {
          id: 'whitelabel',
          completed: status.steps.whitelabel,
          label: t('ONBOARDING_STEP_WHITELABEL'),
          description: t('ONBOARDING_DESC_WHITELABEL'),
          action: () => history.push(CONFIGURATION),
          actionLabel: t('ONBOARDING_ACTION_CONFIGURE'),
        },
        {
          id: 'evaluation',
          completed: status.steps.evaluation,
          label: t('ONBOARDING_STEP_EVALUATION'),
          description: t('ONBOARDING_DESC_EVALUATION'),
          action: () => history.push(CREATE_EVALUATION),
          actionLabel: t('ONBOARDING_ACTION_CREATE'),
        },
        {
          id: 'invitation',
          completed: status.steps.invitation,
          label: t('ONBOARDING_STEP_INVITATION'),
          description: t('ONBOARDING_DESC_INVITATION'),
          action: handleInviteAction,
          actionLabel: t('ONBOARDING_ACTION_INVITE'),
        },
      ];

      const completed = stepsConfig.filter((step) => step.completed).length;

      if (isMountedRef.current) {
        setSteps(stepsConfig);
        setCompletedCount(completed);
      }
    } catch (error) {
      if (isMountedRef.current) {
        notification.error({
          message: t('ONBOARDING_ERROR_LOADING'),
          placement: 'topRight',
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!workspaceId) return;

    fetchOnboardingStatus();
  }, [workspaceId]);

  const handleDismiss = () => {
    dismissOnboarding(workspaceId, userId);
    if (onDismiss) {
      onDismiss();
    }
  };

  if (loading) {
    return (
      <Card className="onboarding-card" bordered={false}>
        <div className="onboarding-loading">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  const progressPercent = Math.round((completedCount / steps.length) * 100);

  return (
    <Card
      className="onboarding-card"
      bordered={false}
      title={
        <div className="onboarding-header">
          <div className="header-title">
            <RocketOutlined className="header-icon" />
            <span>{t('ONBOARDING_TITLE')}</span>
          </div>
          <Button type="text" size="small" onClick={handleDismiss} className="dismiss-button">
            {t('ONBOARDING_DISMISS')}
          </Button>
        </div>
      }
    >
      <div className="onboarding-content">
        <div className="progress-section">
          <div className="progress-text">
            {completedCount}
            {t('ONBOARDING_STEPS_COUNTER_SEPARATOR')}
            {steps.length} {t('ONBOARDING_STEPS_COMPLETED')} - {progressPercent}%
          </div>
          <Progress percent={progressPercent} strokeColor="#4987be" showInfo={false} />
        </div>

        <div className="steps-list">
          {steps.map((step, index) => {
            const previousStepsCompleted =
              index === 0 || steps.slice(0, index).every((s) => s.completed);
            const isButtonDisabled = !previousStepsCompleted;

            return (
              <div
                key={step.id}
                className={`step-item ${step.completed ? 'completed' : 'pending'}`}
              >
                <div className="step-status">
                  {step.completed ? (
                    <CheckCircleOutlined className="check-icon" />
                  ) : (
                    <div className="empty-circle" />
                  )}
                </div>
                <div className="step-content">
                  <div className="step-label">{step.label}</div>
                  <div className="step-description">{step.description}</div>
                </div>
                {!step.completed && (
                  <Button
                    type="primary"
                    size="small"
                    className="step-action-button"
                    onClick={step.action}
                    disabled={isButtonDisabled}
                  >
                    {step.actionLabel}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

export default PartnerOnboarding;
